import { type LucideIcon, Send, Timer, Trash2 } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import type { ReportingEntryStatus } from "@/shared/cache/slices/reportingEntries";
import { cn } from "@/shared/lib/utils";

const CHIPS: Array<{
  status: ReportingEntryStatus;
  label: string;
  Icon: LucideIcon;
  active: string;
  hover: string;
}> = [
  {
    status: "tracked",
    label: "Tracked",
    Icon: Timer,
    active:
      "border-amber-500/60 bg-amber-50 text-amber-700 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300 dark:hover:bg-amber-950/60",
    hover: "hover:text-amber-700 dark:hover:text-amber-300",
  },
  {
    status: "exported",
    label: "Exported",
    Icon: Send,
    active:
      "border-green-500/60 bg-green-50 text-green-700 hover:bg-green-100 dark:border-green-700 dark:bg-green-950/40 dark:text-green-300 dark:hover:bg-green-950/60",
    hover: "hover:text-green-700 dark:hover:text-green-300",
  },
  {
    status: "deleted",
    label: "Deleted",
    Icon: Trash2,
    active:
      "border-red-500/60 bg-red-50 text-red-700 hover:bg-red-100 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300 dark:hover:bg-red-950/60",
    hover: "hover:text-red-700 dark:hover:text-red-300",
  },
];

interface StatusFilterChipsProps {
  active: Set<ReportingEntryStatus>;
  onToggle: (status: ReportingEntryStatus) => void;
  statuses?: ReportingEntryStatus[];
}

export function StatusFilterChips({ active, onToggle, statuses }: StatusFilterChipsProps) {
  const chips = statuses ? CHIPS.filter((c) => statuses.includes(c.status)) : CHIPS;
  return (
    <div className="flex items-center gap-1.5">
      {chips.map(({ status, label, Icon, active: activeCls, hover }) => {
        const isActive = active.has(status);
        return (
          <Button
            key={status}
            type="button"
            variant="outline"
            size="sm"
            aria-pressed={isActive}
            onClick={() => onToggle(status)}
            className={cn(
              "h-7 sm:h-8 gap-1.5 px-2.5 sm:px-3 text-[11px] sm:text-xs font-medium transition-colors",
              isActive
                ? activeCls
                : cn("border-border bg-card text-muted-foreground", hover),
            )}
          >
            <Icon className="size-3.5" />
            {label}
          </Button>
        );
      })}
    </div>
  );
}
